import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { UpdateMobileAppSettingsPayload } from "@/react/api/mobileAppSettingsApi";
import { mobileAppSettingsApi } from "@/react/api/mobileAppSettingsApi";
import type { MobileHomeSegment } from "@/react/types/MobileHomeSegment";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { useToast } from "@circle-react-uikit/ToastV2";

export interface DataResponse {
  community_setting: {
    ios_app_enabled: boolean;
    show_ios_app_banner: boolean;
    mobile_home_segments: MobileHomeSegment[];
  };
}

export const SHOW_QUERY_KEY = "mobileAppSettings";

export const useMobileAppSettings = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isLoading, data } = useQuery<DataResponse>(
    SHOW_QUERY_KEY,
    mobileAppSettingsApi.show,
  );

  const { mobile_home_segments: mobileHomeSegments, ...rest } =
    data?.community_setting || {
      mobile_home_segments: [],
    };
  const defaultValues = { community_setting: rest };

  const { mutate, isLoading: isSaving } = useMutation<
    unknown,
    ApiError,
    UpdateMobileAppSettingsPayload
  >(
    (payload: UpdateMobileAppSettingsPayload) =>
      mobileAppSettingsApi.update(payload),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(SHOW_QUERY_KEY);
        toast.success(t("settings.mobile_app.messages.success"));
      },
      onError: (error: { message: string }) => {
        toast.error(error.message);
      },
    },
  );

  return { defaultValues, mobileHomeSegments, isLoading, mutate, isSaving };
};
